import { useState } from "react";
import { Link, useLocation } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  CURRENT_CONSENT_VERSION,
} from "@/lib/store";

/**
 * First-launch consent gate.
 *
 * New sisters must confirm five things before the app opens to them:
 *   1. They are 18 or older.
 *   2. They have read and accept Terms of Service.
 *   3. They have read and accept Privacy Policy.
 *   4. They have read and accept the Medical and Wellness Disclaimer.
 *   5. They have read and accept Community Guidelines.
 *
 * Each row links to the matching /legal/:slug doc so a sister can read
 * before she ticks. On submit, we record an audit row in legalConsents
 * and route to Today.
 */

interface ConsentRow {
  key: "adult" | "tos" | "privacy" | "disclaimer" | "community";
  label: string;
  helper: string;
  slug?: string;
}

const ROWS: ConsentRow[] = [
  {
    key: "adult",
    label: "I confirm I am 18 or older",
    helper: "Move Nourish Flourish is built for adult sisters.",
  },
  {
    key: "tos",
    label: "I have read and accept the Terms of Service",
    helper: "How we work together, what to expect, and how disputes are handled.",
    slug: "terms",
  },
  {
    key: "privacy",
    label: "I have read and accept the Privacy Policy",
    helper: "What we collect, why we collect it, and your rights over your data.",
    slug: "privacy",
  },
  {
    key: "disclaimer",
    label: "I have read the Medical and Wellness Disclaimer",
    helper: "This app supports your wellness journey, it does not replace your doctor.",
    slug: "disclaimer",
  },
  {
    key: "community",
    label: "I will follow the Community Guidelines",
    helper: "Grace, kindness, and confidentiality in every interaction.",
    slug: "community",
  },
];

export default function ConsentGate() {
  const [, navigate] = useLocation();
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: this page WRITES a legal-consent audit row.
  // Recording that row against the seeded demo account while the real
  // identity is still resolving would produce a false consent record —
  // either missing for the real sister, or fabricated for Hannah's account.
  const userId = me?.id ?? null;
  const alreadyAccepted = useStoreQuery(() =>
    userId ? queries.hasAcceptedCurrentConsent(userId) : false,
  );
  const recordConsent = useStoreMutation(mutations.recordLegalConsent);

  const [checked, setChecked] = useState<Record<ConsentRow["key"], boolean>>({
    adult: false,
    tos: false,
    privacy: false,
    disclaimer: false,
    community: false,
  });

  const allChecked = Object.values(checked).every(Boolean);

  if (!userId) {
    return (
      <div className="pb-24">
        <ScreenHeader
          variant="forest"
          eyebrow="Welcome, sister"
          title="Before we begin"
          subtitle="Finishing setting up your account..."
        />
        <div className="px-5 mt-2">
          <GlassCard organic padding={16}>
            <p className="mnf-body" style={{ color: "var(--mnf-body)", fontSize: 14 }}>
              We're still loading your account, so we can't safely record your
              agreements yet. If this doesn't clear in a moment, please sign in
              again.
            </p>
          </GlassCard>
        </div>
      </div>
    );
  }

  const handleSubmit = () => {
    if (!allChecked) return;
    recordConsent({
      userId,
      version: CURRENT_CONSENT_VERSION,
      acceptedTermsOfService: checked.tos,
      acceptedPrivacyPolicy: checked.privacy,
      acceptedMedicalDisclaimer: checked.disclaimer,
      acceptedCommunityGuidelines: checked.community,
      confirmedAdult: checked.adult,
    });
    navigate("/");
  };

  return (
    <div className="pb-24">
      <ScreenHeader
        variant="forest"
        eyebrow="Welcome, sister"
        title="Before we begin"
        subtitle="A few quick agreements so we can walk together with clarity and care."
      />

      {alreadyAccepted ? (
        <div className="px-5 mt-2">
          <GlassCard organic padding={16}>
            <p className="mnf-body" style={{ color: "var(--mnf-body)", fontSize: 14 }}>
              You already accepted the April 27, 2026 version. You can review
              every policy any time from{" "}
              <Link href="/legal" className="underline" data-testid="link-policies-hub">
                our policies page
              </Link>
              .
            </p>
            <div className="mt-3">
              <Btn
                variant="primary"
                block
                onClick={() => navigate("/")}
                data-testid="button-continue-home"
              >
                Continue to Today
              </Btn>
            </div>
          </GlassCard>
        </div>
      ) : (
        <>
          <div className="px-5 mt-2 space-y-2.5">
            {ROWS.map((row) => (
              <GlassCard key={row.key} organic padding={14}>
                <label
                  className="flex items-start gap-3 cursor-pointer"
                  data-testid={`row-consent-${row.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked[row.key]}
                    onChange={(e) =>
                      setChecked((c) => ({ ...c, [row.key]: e.target.checked }))
                    }
                    className="mt-1 shrink-0"
                    style={{ width: 20, height: 20, accentColor: "var(--mnf-f)" }}
                    data-testid={`checkbox-consent-${row.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className="mnf-serif"
                      style={{
                        color: "var(--mnf-f)",
                        fontSize: 16,
                        lineHeight: 1.3,
                        fontWeight: 600,
                      }}
                    >
                      {row.label}
                    </p>
                    <p
                      className="mnf-body mt-1"
                      style={{ color: "var(--mnf-body)", fontSize: 13, lineHeight: 1.4 }}
                    >
                      {row.helper}
                    </p>
                    {row.slug && (
                      <Link
                        href={`/legal/${row.slug}`}
                        className="mnf-body inline-flex items-center gap-1 mt-1.5"
                        style={{
                          color: "var(--mnf-coral)",
                          fontSize: 13,
                          textDecoration: "none",
                        }}
                        data-testid={`link-read-${row.slug}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Read it
                        <Icon name="arrow_forward" size={14} weight={500} />
                      </Link>
                    )}
                  </div>
                </label>
              </GlassCard>
            ))}
          </div>

          <div className="px-5 mt-5">
            <p
              className="mnf-body"
              style={{ color: "var(--mnf-body)", fontSize: 12, lineHeight: 1.5, opacity: 0.8 }}
            >
              By tapping the button below, you confirm each agreement above and
              acknowledge that we will keep a record of your acceptance.
            </p>
          </div>

          <div className="px-5 mt-3">
            <Btn
              variant="primary"
              block
              onClick={handleSubmit}
              disabled={!allChecked}
              data-testid="button-accept-and-begin"
            >
              I agree, begin
            </Btn>
          </div>

          <div className="px-5 mt-3 text-center">
            <Link
              href="/legal"
              className="mnf-body"
              style={{ color: "var(--mnf-body)", fontSize: 13 }}
              data-testid="link-browse-policies"
            >
              Browse all policies
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
